import { useState, useEffect } from 'react';
import { useUserStore } from '../stores/userStore';
import { useLocation } from 'react-router-dom';

// Interval in milliseconds (5 minutes = 300000ms)
const PROMOTION_INTERVAL = 300000;

export function usePromotions() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [lastShown, setLastShown] = useState<number | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number>(24 * 60 * 60 * 1000); // 24 hours in milliseconds
  const isPro = useUserStore(state => state.isPro);
  const isPremium = useUserStore(state => state.isPremium);
  const isAdmin = useUserStore(state => state.isAdmin);
  const location = useLocation();
  
  // Check if user has access to Pro (either Pro user or Admin)
  const hasProAccess = isPro() || isAdmin();
  const hasPremiumAccess = isPremium() || hasProAccess;
  
  // Don't show promotions on pricing page
  const isPricingPage = location.pathname.includes('/precos');

  useEffect(() => {
    // Don't show promotions to Admin users or on the pricing page
    if (isAdmin() || isPricingPage) return;
    
    // Get last shown timestamp from localStorage
    const storedLastShown = localStorage.getItem('lastPromotionShown');
    const lastShownTime = storedLastShown ? parseInt(storedLastShown, 10) : null;
    setLastShown(lastShownTime);
    
    // Get remaining time from localStorage or set default
    const storedTimeRemaining = localStorage.getItem('promotionTimeRemaining');
    if (storedTimeRemaining) {
      setTimeRemaining(parseInt(storedTimeRemaining, 10));
    }
    
    // Check if we should show the promotion immediately
    const shouldShowImmediately = () => {
      if (!lastShownTime) return true;
      
      const now = Date.now();
      return now - lastShownTime > PROMOTION_INTERVAL;
    };
    
    // Show immediately if needed
    if (shouldShowImmediately()) {
      // Determine which promotion to show based on user subscription
      if (hasPremiumAccess && !hasProAccess) {
        // Premium users see Pro upgrade
        setShowPrompt(true);
        setShowBanner(false);
      } else if (!hasPremiumAccess) {
        // Free users see Premium upgrade
        const showPromptInstead = Math.random() > 0.5;
        
        if (showPromptInstead) {
          setShowPrompt(true);
          setShowBanner(false);
        } else {
          setShowPrompt(false);
          setShowBanner(true);
        }
      }
      
      setLastShown(Date.now());
      localStorage.setItem('lastPromotionShown', Date.now().toString());
    }
    
    // Set up interval to show promotion periodically
    const interval = setInterval(() => {
      // Determine which promotion to show based on user subscription
      if (hasPremiumAccess && !hasProAccess) {
        // Premium users see Pro upgrade
        setShowPrompt(true);
        setShowBanner(false);
      } else if (!hasPremiumAccess) {
        // Free users see Premium upgrade
        const showPromptInstead = Math.random() > 0.5;
        
        if (showPromptInstead) {
          setShowPrompt(true);
          setShowBanner(false);
        } else {
          setShowPrompt(false);
          setShowBanner(true);
        }
      }
      
      setLastShown(Date.now());
      localStorage.setItem('lastPromotionShown', Date.now().toString());
    }, PROMOTION_INTERVAL);
    
    // Set up timer countdown
    const timerInterval = setInterval(() => {
      setTimeRemaining(prev => {
        const newTime = Math.max(0, prev - 1000); // Decrease by 1 second
        localStorage.setItem('promotionTimeRemaining', newTime.toString());
        
        // Reset timer when it reaches zero
        if (newTime === 0) {
          return 24 * 60 * 60 * 1000; // Reset to 24 hours
        }
        
        return newTime;
      });
    }, 1000);
    
    return () => {
      clearInterval(interval);
      clearInterval(timerInterval);
    };
  }, [hasProAccess, hasPremiumAccess, isPricingPage, isAdmin]);

  const handleClose = () => {
    setShowPrompt(false);
    setShowBanner(false);
    setLastShown(Date.now());
    localStorage.setItem('lastPromotionShown', Date.now().toString());
  };

  // Format time remaining for display (HH:MM:SS)
  const formatTimeRemaining = (): string => {
    const hours = Math.floor(timeRemaining / (60 * 60 * 1000));
    const minutes = Math.floor((timeRemaining % (60 * 60 * 1000)) / (60 * 1000));
    const seconds = Math.floor((timeRemaining % (60 * 1000)) / 1000);
    
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  return {
    showPrompt,
    showBanner,
    handleClose,
    timeRemaining,
    formattedTimeRemaining: formatTimeRemaining()
  };
}